import React from 'react';
import Tab from 'Components/DesignSystem/Tab/Tab';
import { TabItem } from 'types/DesignSystem/Tab';
import { TTabsProps } from '../isr';

/**
 * The builder's five-tab strip, now an adapter over the design-system `Tab`.
 *
 * **What the hand-rolled version got wrong in dark mode:** the inactive label set
 * NO ink class at all, so it inherited the page's ambient colour — near-black on
 * the dark ground, i.e. four of the five tabs were invisible. Active/hover chrome
 * was the raw literal `#0747A6` and the loader-disabled label `#DFE0E6`, both
 * light-only. DS `Tab` colours every state from role tokens and brings the roving
 * tabindex, arrow-key navigation and the animated indicator with it.
 *
 * **Preserved contracts:**
 * - The caller-facing props are unchanged (`ISRMainPage` and its tests mock this
 *   component by exactly this interface).
 * - Each tab keeps its DOM id `${id}-tab-<label-slug>` — the selector the
 *   Playwright contract in `okf/isr/e2e.md` § "Tabs" is written against — via
 *   `TabItem.htmlId`, and now also carries the SAME string as its `data-testid`
 *   (the old markup shared one `components-isrtabs-div-1` across all five tabs).
 * - `showLoaderUi` disables ONLY index 2 (Integrations) and index 3 (User
 *   Interface) and blocks their clicks; DS `disabled` also announces it
 *   (`aria-disabled`, ink-disabled label).
 *
 * `tw--mb-px` pulls the tablist down one pixel so its `border-b-edge` lands
 * exactly on the full-width rule the `#isr-tabs-wrapper` div in `ISRMainPage`
 * draws — one continuous line instead of a doubled band under the centred strip.
 */
const ISRTabs = ({ id, tabLabels, activeTab, onTabClick, showLoaderUi }: TTabsProps) => {
    const tabs: TabItem[] = tabLabels.map((label, index) => {
        const slug = `${id}-tab-${label.toLowerCase().replace(/\s+/g, '-')}`;
        return {
            id: index,
            label,
            htmlId: slug,
            'data-testid': slug,
            disabled: showLoaderUi && (index === 2 || index === 3),
        };
    });

    return (
        <div id={id} className="tw--mb-px">
            <Tab
                data-testid={id}
                tabs={tabs}
                activeTabId={activeTab}
                onChange={(tabId) => onTabClick(Number(tabId))}
            />
        </div>
    );
};

export default ISRTabs;
